import * as React from 'react';
import { useMount } from 'react-use';
import axios, { AxiosRequestConfig } from 'axios';
import { useQueryClient } from 'react-query';
import { Location } from 'history';
import { useLocation, useHistory } from 'react-router';
import { useToast } from 'my-account/toast';

import {
  GetPasswordTokenBodyGrantTypeEnum,
  GetTokenWithPasswordResetBodyGrantTypeEnum,
  GetTokenWithSalesforceSSOBodyGrantTypeEnum,
  RegisteredUser,
} from '__generated-api__';
import {
  getAuthData,
  removeAuthData,
  setAuthData,
  prepareAuthData,
  addRefreshTokenInterceptor,
  removeRefreshTokenInterceptor,
  getOriginalAuthData,
  setOriginalAuthData,
  removeOriginalAuthData,
  OriginalAuthData,
  prepareImpersonationAuthData,
  setLoggingOut,
  setOnImpersonationAccessInvalidated,
} from 'api/auth';
import api, { API_CLIENT_ID, API_CLIENT_SECRET } from 'api';
import { useMutation, UseMutationResult } from 'hooks/query';
import { defineAbilityFor, AuthorizationContext } from 'auth/authorization';

export enum AuthStatus {
  LoggedOut = 'logged-out',
  LoggingIn = 'logging-in',
  LoggedIn = 'logged-in',
  LoggingOut = 'logging-out',
  TwoFactorRequired = 'two-factor-required',
}

const initialAuthData = getAuthData();

type AuthenticationContextState =
  | {
      status: AuthStatus.LoggedIn;
      currentUser: RegisteredUser;
      impersonatingUser?: OriginalAuthData['user'];
    }
  | {
      status: AuthStatus.LoggingOut;
      currentUser: RegisteredUser;
    }
  | {
      status: AuthStatus.LoggedOut;
    }
  | {
      status: AuthStatus.LoggingIn;
    }
  | {
      status: AuthStatus.TwoFactorRequired;
      currentUser: RegisteredUser;
    };

type AuthenticationContextValue = AuthenticationContextState & {
  login: (username: string, password: string) => Promise<void>;
  loginSalesforce: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  verifyReset: (token: string, email: string) => Promise<void>;
  loginMutationState: UseMutationResult<typeof api.auth.getAuthToken>[1];
  updateUserData: (data: Partial<Pick<RegisteredUser, 'first_name' | 'last_name' | 'email' | 'image'>>) => void;
  currentUserId: number;
  verifyTwoFactor: (code: string) => Promise<void>;
  resend2FA: () => Promise<void>;
  startImpersonating: (userId: number) => Promise<void>;
  stopImpersonating: () => Promise<void>;
};

const AuthenticationContext = React.createContext<AuthenticationContextValue>({
  status: AuthStatus.LoggingIn,
  currentUserId: 0,
  login: () => Promise.resolve(),
  loginSalesforce: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  verifyReset: () => Promise.resolve(),
  loginMutationState: {
    context: undefined,
    data: undefined,
    error: undefined,
    failureCount: 0,
    isError: false,
    isIdle: true,
    isLoading: false,
    isPaused: false,
    isSuccess: false,
    reset: () => {},
    status: 'idle',
    variables: undefined,
  },
  updateUserData: () => {},
  verifyTwoFactor: () => Promise.resolve(),
  resend2FA: () => Promise.resolve(),
  startImpersonating: () => Promise.resolve(),
  stopImpersonating: () => Promise.resolve(),
});

export const AuthProvider: React.FC = ({ children }) => {
  const location = useLocation();
  const [state, setState] = React.useState<AuthenticationContextState>({
    status:
      typeof initialAuthData !== 'undefined' && !location.pathname.startsWith('/auth/verify-reset')
        ? AuthStatus.LoggingIn
        : AuthStatus.LoggedOut,
    currentUser: undefined,
  });
  const [getAuthToken, loginMutationState] = useMutation(api.auth.getAuthToken);
  const { reset: resetLoginMutation } = loginMutationState;

  const [apiStartImpersonating] = useMutation(api.impersonate.impersonate);
  const [apiStopImpersonating] = useMutation(api.impersonate.stopImpersonate);
  const [verifyTwoFactorToken] = useMutation(api.auth.verifyTwoFactor);
  const [resendTwoFactorCode] = useMutation(api.auth.resendTwoFactorCode);
  const queryClient = useQueryClient();
  const prevLocation = React.useRef<Location<unknown> | undefined>(undefined);
  const initialLoginPromise = React.useRef<{ promise: Promise<void>; cancel: () => void } | undefined>(undefined);
  const history = useHistory();
  const toast = useToast();

  // Extract currentUser safely for dependency arrays
  const currentUser =
    state.status === AuthStatus.LoggedIn ||
    state.status === AuthStatus.LoggingOut ||
    state.status === AuthStatus.TwoFactorRequired
      ? state.currentUser
      : undefined;

  const initialLogin = React.useCallback(() => {
    if (typeof initialLoginPromise.current === 'undefined') {
      if (
        typeof initialAuthData === 'undefined' ||
        location.pathname.startsWith('/auth/verify-reset') ||
        state.status === AuthStatus.TwoFactorRequired
      ) {
        return;
      }

      setState({ status: AuthStatus.LoggingIn });

      const cancelTokenSource = axios.CancelToken.source();
      const reqOptions: AxiosRequestConfig = { cancelToken: cancelTokenSource.token };
      const promise = api.user
        .getCurrentUser(undefined, reqOptions)
        .then((res) => {
          const originalAuthData = getOriginalAuthData();
          setState({ status: AuthStatus.LoggedIn, currentUser: res.data, impersonatingUser: originalAuthData?.user });
          initialLoginPromise.current = undefined;
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            removeAuthData();
            setState({ status: AuthStatus.LoggedOut });
          }

          initialLoginPromise.current = undefined;
        });

      const cancel = () => cancelTokenSource.cancel();

      initialLoginPromise.current = { promise, cancel };
    }

    return initialLoginPromise.current;
  }, [location, state.status]);

  useMount(() => {
    const promiseData = initialLogin();

    return () => {
      promiseData?.cancel();
    };
  });

  React.useEffect(() => {
    if (
      prevLocation.current &&
      prevLocation.current.pathname.startsWith('/auth/verify-reset') &&
      location.pathname.startsWith('/auth/login') &&
      state.status !== AuthStatus.TwoFactorRequired
    ) {
      initialLogin();
    }

    prevLocation.current = location;
  }, [location, initialLogin, state.status]);

  const login = React.useCallback(
    (username: string, password: string) => {
      // TODO: add cancel token
      setState({ status: AuthStatus.LoggingIn });

      return getAuthToken([
        {
          getTokenBody: {
            client_id: API_CLIENT_ID,
            client_secret: API_CLIENT_SECRET,
            grant_type: GetPasswordTokenBodyGrantTypeEnum.Password,
            username,
            password,
          },
        },
      ])
        .then((res) => {
          removeOriginalAuthData();
          const authData = prepareAuthData(res.data);
          setAuthData(authData);
          addRefreshTokenInterceptor(authData.refreshToken);
          if (authData.two_factor_required) {
            setState({ status: AuthStatus.TwoFactorRequired, currentUser: res.data.user });
          } else {
            setState({ status: AuthStatus.LoggedIn, currentUser: res.data.user });
          }
        })
        .catch((error) => {
          removeRefreshTokenInterceptor();
          removeAuthData();
          setState({ status: AuthStatus.LoggedOut });
          throw error;
        });
    },
    [getAuthToken]
  );

  const verifyTwoFactor = React.useCallback(
    (code: string) => {
      return verifyTwoFactorToken([
        {
          verifyTwoFactorBody: {
            code,
          },
        },
      ])
        .then((res) => {
          if (state.status === AuthStatus.TwoFactorRequired) {
            const authData = getAuthData();
            if (res.data.status === 'verified' && authData) {
              setAuthData(authData);
              addRefreshTokenInterceptor(authData.refreshToken);
              setState({ status: AuthStatus.LoggedIn, currentUser: currentUser! });
              //console.log(history.location.state);
            } else {
              removeRefreshTokenInterceptor();
              removeAuthData();
              setState({ status: AuthStatus.LoggedOut });
              //console.log(res.data);
            }
          }
        })
        .catch((error) => {
          //console.log(error.response.data);
          removeRefreshTokenInterceptor();
          removeAuthData();
          setState({ status: AuthStatus.LoggedOut });
          history.push('/auth/login');
          throw error;
        });
    },
    [verifyTwoFactorToken, history, state.status, currentUser]
  );

  const resend2FA = React.useCallback(() => {
    return resendTwoFactorCode([undefined])
      .then((res) => {
        //console.log('2fa code resent');
        toast.notify({
          type: 'success',
          title: 'Success',
          message: 'Two-Factor Authentication code resent. Please check your email for the new code.',
        });
      })
      .catch((error) => {
        //console.log('2fa code failed to resend');
        removeRefreshTokenInterceptor();
        removeAuthData();
        setState({ status: AuthStatus.LoggedOut });
        toast.notify({
          type: 'error',
          title: 'Error',
          message: 'Failed to resend Two-Factor Authentication code. Please login again.',
        });
        history.push('/auth/login');
      });
  }, [resendTwoFactorCode, history, toast]);

  const loginSalesforce = React.useCallback(
    (token: string) => {
      // TODO: add cancel token
      setState({ status: AuthStatus.LoggingIn });

      return getAuthToken([
        {
          getTokenBody: {
            client_id: API_CLIENT_ID,
            client_secret: API_CLIENT_SECRET,
            grant_type: GetTokenWithSalesforceSSOBodyGrantTypeEnum.SalesforceSso,
            token,
          },
        },
      ])
        .then((res) => {
          const authData = prepareAuthData(res.data);
          setAuthData(authData);
          addRefreshTokenInterceptor(authData.refreshToken);
          setState({ status: AuthStatus.LoggedIn, currentUser: res.data.user });
        })
        .catch((error) => {
          removeRefreshTokenInterceptor();
          removeAuthData();
          setState({ status: AuthStatus.LoggedOut });
          throw error;
        });
    },
    [getAuthToken]
  );

  const verifyReset = React.useCallback(
    (token: string, email: string) => {
      // TODO: add cancel token
      return getAuthToken([
        {
          getTokenBody: {
            client_id: API_CLIENT_ID,
            client_secret: API_CLIENT_SECRET,
            grant_type: GetTokenWithPasswordResetBodyGrantTypeEnum.ResetPasswordGrant,
            token,
            email,
          },
        },
      ])
        .then((res) => {
          const authData = prepareAuthData(res.data);
          setAuthData(authData);
          addRefreshTokenInterceptor(authData.refreshToken);
          setState({ status: AuthStatus.LoggedIn, currentUser: res.data.user });
        })
        .catch((error) => {
          removeRefreshTokenInterceptor();
          removeAuthData();
          setState({ status: AuthStatus.LoggedOut });
          throw error;
        });
    },
    [getAuthToken]
  );

  const logout = React.useCallback(async () => {
    if (state.status !== AuthStatus.LoggedIn) {
      return;
    }
    setLoggingOut(true);
    setState({ status: AuthStatus.LoggingOut, currentUser: state.currentUser });
    removeRefreshTokenInterceptor();
    removeAuthData();
    removeOriginalAuthData();
    await queryClient.invalidateQueries();
    setState({ status: AuthStatus.LoggedOut });
    setLoggingOut(false);
    history.push('/auth/login');
  }, [queryClient, state, history]);

  const handleInvalidImpersonationAccess = React.useCallback(() => {
    resetLoginMutation();
    removeRefreshTokenInterceptor();
    removeAuthData();
    removeOriginalAuthData();
    queryClient.clear();
    setState({ status: AuthStatus.LoggedOut });
    setLoggingOut(false);
    if (!location.pathname.startsWith('/auth/login')) {
      history.replace('/auth/login');
    }
  }, [queryClient, history, location.pathname, resetLoginMutation]);

  React.useEffect(() => {
    setOnImpersonationAccessInvalidated(handleInvalidImpersonationAccess);
    return () => setOnImpersonationAccessInvalidated(undefined);
  }, [handleInvalidImpersonationAccess]);

  const updateUserData = React.useCallback(
    (data: Partial<Pick<RegisteredUser, 'first_name' | 'last_name' | 'email' | 'image'>>) => {
      if (state.status !== AuthStatus.LoggedIn) {
        return;
      }

      setState((prevValue) => {
        if (prevValue.status !== AuthStatus.LoggedIn) {
          return prevValue;
        }

        return { ...prevValue, currentUser: { ...prevValue.currentUser, ...data } };
      });
    },
    [state]
  );

  const startImpersonating = React.useCallback(
    (userId: number) => {
      const currentAuthData = getAuthData();

      if (state.status !== AuthStatus.LoggedIn || !currentAuthData) {
        toast.notify({
          type: 'error',
          title: 'Error',
          message: 'Must be logged in to start impersonating.',
        });
        throw new Error('Must be logged in to start impersonating.');
      }

      return apiStartImpersonating([{ userId }])
        .then((res) => {
          const impersonationAuthData = prepareImpersonationAuthData(res.data);
          const originalAuthData = {
            authData: currentAuthData,
            user: {
              id: state.currentUser.id,
              first_name: state.currentUser.first_name,
              last_name: state.currentUser.last_name,
            },
          };

          setOriginalAuthData(originalAuthData);
          setAuthData(impersonationAuthData);
          addRefreshTokenInterceptor(impersonationAuthData.refreshToken);
          queryClient.clear();
          setState({
            status: AuthStatus.LoggedIn,
            currentUser: res.data.user,
            impersonatingUser: originalAuthData.user,
          });

          toast.notify({
            type: 'success',
            title: 'Success',
            message: "You've successfully started impersonating.",
          });
          history.push('/');
        })
        .catch((error) => {
          toast.notify({
            type: 'error',
            title: 'Error',
            message: 'API error, could not start impersonating.',
          });
          throw error;
        });
    },
    [apiStartImpersonating, queryClient, history, state, toast]
  );

  const stopImpersonating = React.useCallback(() => {
    if (state.status !== AuthStatus.LoggedIn) {
      toast.notify({
        type: 'error',
        title: 'Error',
        message: 'Must be logged in to stop impersonating.',
      });
      throw new Error('Must be logged in to stop impersonating.');
    }

    return apiStopImpersonating([undefined])
      .then((res) => {
        /**
         * Impersonation token is now revoked.
         *
         * This could return the wrong admin user in an edge case if multiple admins impersonated
         * the same user in the same time, so we still want to call getCurrentUser().
         */
      })
      .catch((error) => {
        /**
         * We could not stop impersonating.
         * But the original auth data might still be valid, try to getCurrentUser() with original auth data.
         */
      })
      .then((res) => {
        const originalAuthData = getOriginalAuthData();
        removeOriginalAuthData();

        if (!originalAuthData) {
          throw new Error('There is no original auth data.');
        }

        setAuthData(originalAuthData.authData);
        addRefreshTokenInterceptor(originalAuthData.authData.refreshToken);

        return api.user.getCurrentUser();
      })
      .then(async (res) => {
        queryClient.clear();
        setState({ status: AuthStatus.LoggedIn, currentUser: res.data });
        toast.notify({
          type: 'success',
          title: 'Success',
          message: "You've successfully stopped impersonating.",
        });

        if (window.location.pathname.startsWith('/my-account')) {
          history.push('/');
        } else {
          // we're somewhere else on the site, redirect to dashboard
          window.location.href = '/my-account/';
        }
      })
      .catch(async (error) => {
        removeRefreshTokenInterceptor();
        removeAuthData();
        queryClient.clear();
        setState({ status: AuthStatus.LoggedOut });
        throw error;
      });
  }, [apiStopImpersonating, queryClient, history, state, toast]);

  const authenticationState: AuthenticationContextValue = {
    ...state,
    login,
    loginSalesforce,
    loginMutationState,
    logout,
    verifyReset,
    updateUserData,
    verifyTwoFactor,
    resend2FA,
    startImpersonating,
    stopImpersonating,
    currentUserId: state.status === AuthStatus.TwoFactorRequired ? state.currentUser.id : 0,
  };

  const ability = React.useMemo(
    () =>
      defineAbilityFor(
        state.status === AuthStatus.LoggedIn || state.status === AuthStatus.LoggingOut ? state.currentUser : undefined
      ),
    [state]
  );

  return (
    <AuthenticationContext.Provider value={authenticationState}>
      <AuthorizationContext.Provider value={ability}>{children}</AuthorizationContext.Provider>
    </AuthenticationContext.Provider>
  );
};

export function useAuth() {
  return React.useContext(AuthenticationContext);
}

export function useAuthenticatedUser() {
  const auth = useAuth();

  if (auth.status === AuthStatus.LoggedIn || auth.status === AuthStatus.LoggingOut) {
    return auth.currentUser;
  }

  throw new Error('Invalid use of "useAuthenticatedUser" hook.');
}

export function useAbility() {
  return React.useContext(AuthorizationContext);
}
